import { HealthLang } from "../health/healthTypes";

/** Strip locale prefix and trailing slash for nav comparisons. */
export function normalizeNavPathname(pathname: string): string {
    let path = pathname;
    if (path.startsWith("/tw")) {
        path = path.slice(3) || "/";
    } else if (path.startsWith("/jp")) {
        path = path.slice(3) || "/";
    }
    if (!path.startsWith("/")) {
        path = `/${path}`;
    }
    if (path.length > 1 && path.endsWith("/")) {
        path = path.slice(0, -1);
    }
    return path;
}

export function localeNavPrefix(lang: HealthLang): string {
    if (lang === "Zh") return "/tw";
    if (lang === "Jp") return "/jp";
    return "";
}

export function langFromPathname(pathname: string): HealthLang {
    if (pathname.startsWith("/tw")) return "Zh";
    if (pathname.startsWith("/jp")) return "Jp";
    return "En";
}

/** Router location.hash can lag after in-page hash scroll (history.replaceState). */
export function getEffectiveLocationHash(routerHash = ""): string {
    if (routerHash) {
        return routerHash;
    }
    if (typeof window === "undefined") {
        return "";
    }
    return window.location.hash || "";
}

export function switchLocalePathname(pathname: string, targetLang: HealthLang): string {
    const normalized = normalizeNavPathname(pathname);
    if (normalized === "/") {
        if (targetLang === "Zh") return "/tw";
        if (targetLang === "Jp") return "/jp";
        return "/";
    }
    return buildNavHref(normalized, targetLang);
}

export function buildNavHref(path: string, lang: HealthLang): string {
    const normalized = path.startsWith("/") ? path : `/${path}`;
    const prefix = localeNavPrefix(lang);
    if (normalized === "/") {
        return prefix || "/";
    }
    return `${prefix}${normalized}`;
}

export function navRootSegment(pathname: string): string {
    const segments = normalizeNavPathname(pathname).split("/").filter(Boolean);
    return segments[0] ?? "";
}

export function isNavPathActive(
    pathname: string,
    href: string,
    exact = false
): boolean {
    const current = normalizeNavPathname(pathname);
    const target = normalizeNavPathname(href);
    if (exact) {
        return current === target;
    }
    return current === target || current.startsWith(`${target}/`);
}

export function isAgentsLandingPath(pathname: string): boolean {
    return normalizeNavPathname(pathname) === "/agents";
}

/** White / light hero subsidiary landings — nav uses dark text and logo at page top. */
export function isLightSubsidiaryPath(pathname: string): boolean {
    return (
        pathname.includes("/drugomics") ||
        pathname.includes("/health") ||
        pathname.includes("/education") ||
        isAgentsLandingPath(pathname)
    );
}

const SUBSIDIARY_PATH_MARKERS = ["/drugomics", "/health", "/education"] as const;

/** Desktop nav: show corporate-home escape on subsidiary business-unit routes. */
export function isSubsidiaryBusinessUnitPath(pathname: string): boolean {
    return SUBSIDIARY_PATH_MARKERS.some((marker) => pathname.includes(marker));
}

export function corporateHomeHref(lang: HealthLang): string {
    return buildNavHref("/", lang);
}
